<template>
  <validation-observer
    ref="form"
    #default="{ invalid }"
  >
    <b-modal
      :id="modalName"
      size="lg"
      ok-only
      :ok-title="$t('save')"
      :ok-disabled="submitSpinner || invalid"
      no-close-on-backdrop
      @shown="onShownModal"
      @ok="onSubmit"
      @hidden="hideModal"
    >
      <template #modal-title="{}">
        <span>
          {{ isUpdated ? $t("Update") : $t("Create") }}
        </span>
        <span v-if="isUpdated">{{ form.code }}</span>
      </template>
      <div
        v-if="loader"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-form v-else>
        <b-row>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Code')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Code')"
                vid="code"
                rules="required"
              >
                <b-form-input
                  id="code"
                  v-model="form.code"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Code')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Amount')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Amount')"
                vid="amount"
                rules="required"
              >
                <b-form-input
                  id="amount"
                  v-model="form.amount"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Amount')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group :label="$t('Amount type')">
              <validation-provider
                #default="{ errors }"
                :name="$t('Amount type')"
                vid="amount_type"
                rules="required"
              >
                <v-select
                  v-model="form.amount_type"
                  :options="additionalData.amountTypes"
                  :clearable="false"
                  :reduce="_amountType => _amountType.key"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Ceil')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Ceil')"
                vid="ceil"
              >
                <b-form-input
                  id="ceil"
                  v-model="form.ceil"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Ceil')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Floor')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Floor')"
                vid="floor"
              >
                <b-form-input
                  id="floor"
                  v-model="form.floor"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Floor')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Max use')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Max use')"
                vid="max_use"
              >
                <b-form-input
                  id="max_use"
                  v-model="form.max_use"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Max use')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Max use per user')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Max use per user')"
                vid="max_use_per_user"
              >
                <b-form-input
                  id="max_use_per_user"
                  v-model="form.max_use_per_user"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Max use per user')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group :label="$t('Status')">
              <validation-provider
                #default="{ errors }"
                :name="$t('Status')"
                vid="status"
                rules="required"
              >
                <v-select
                  v-model="form.status"
                  :options="additionalData.statuses"
                  :clearable="false"
                  :reduce="_status => _status.key"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('Expire at')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Expire at')"
                vid="expire_at"
              >
                <date-picker
                  id="expire_at"
                  v-model="form.expire_at"
                  type="datetime"
                  format="YYYY-MM-DD HH:mm"
                  display-format="jYYYY/jMM/jDD HH:mm"
                  color="#28c76f"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Expire at')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>
        <template v-for="(type,key) in additionalData.payableTypes">
          <b-row
            v-if="type === 'App\\Models\\Invoice'"
            :key="key"
          >
            <b-col
              md="6"
              cols="12"
            >
              <b-form-group :label="$t('Packages')">
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Packages')"
                  vid="packages"
                >
                  <v-select
                    v-model="form.options.payable_types[type].packages"
                    :options="additionalData.packages"
                    :clearable="false"
                    multiple
                    label="name"
                    :reduce="_value => _value.id"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              md="6"
              cols="12"
            >
              <b-form-group :label="$t('Products')">
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Products')"
                  vid="products"
                >
                  <v-select
                    v-model="form.options.payable_types[type].products"
                    :options="additionalData.products"
                    :clearable="false"
                    multiple
                    label="name"
                    :reduce="_value => _value.id"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </template>
      </b-form>
      <template #modal-ok="{}">
        <span>
          <b-spinner
            v-if="submitSpinner"
            small
          />
          {{ $t("save") }}
        </span>
      </template>
    </b-modal>
  </validation-observer>
</template>

<script>
import { EditFormLayoutMixin, EditModalMixin } from '../../../../common/mixins/form'

export default {
  name: 'DiscountCodesEditModal',
  mixins: [EditFormLayoutMixin, EditModalMixin],
  data() {
    return {
      routeName: 'discount-codes',
      modalName: 'modal-discount-codes-edit',
      useRouteName: true,
      createAbleFields: [
        'code',
        'amount_type',
        'amount',
        'ceil',
        'floor',
        'max_use',
        'max_use_per_user',
        'users',
        'options',
        'expire_at',
        'status',
      ],
    }
  },
  methods: {
    onShownModal() {
      if (!this.isUpdated) {
        this.form.options = {
          payable_types: {
            'App\\Models\\Invoice': {
              packages: [],
              products: [],
            },
          },
        }
      }
      this.loader = false
    },
  },
}
</script>

<style scoped>

</style>
